import React, { useEffect, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useTrainingStore } from "../../store/store";
import { Training, Trainee, Test } from "../../types/api";
import { useTrainingModule } from "./TrainingModuleContext";
import axiosInstance from "../../common/axiosInstance";

interface TraineeWithResults extends Omit<Trainee, "attendance" | "results"> {
  attendance: Record<string, "Present" | "Absent">;
  preTestScore?: number | null;
  postTestScore?: number | null;
}

interface TestResult {
  traineeId: string;
  score: number;
}

const StartTest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const gridRef = useRef<AgGridReact<TraineeWithResults>>(null);
  const [gridApi, setGridApi] = useState<GridApi<TraineeWithResults> | null>(
    null,
  );
  const [showPopup, setShowPopup] = useState(false);
  const [attendance, setAttendance] = useState<
    Record<string, "Present" | "Absent">
  >({});
  const [training, setTraining] = useState<Training | null>(null);
  const [trainees, setTrainees] = useState<TraineeWithResults[]>([]);
  const [test, setTest] = useState<Test | null>(null);
  const [testCategory, setTestCategory] = useState<"Pre-Test" | "Post-Test">(
    "Pre-Test",
  );
  const [gridKey, setGridKey] = useState(0);
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { trainings, fetchTrainings, updateAttendance } = useTrainingStore();

  const proxyUrl = () => process.env.REACT_APP_API_URL;

  const onGridReady = (params: GridReadyEvent<TraineeWithResults>) => {
    setGridApi(params.api);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        return;
      }

      await fetchTrainings({
        type: "all",
        where: isFixedCategory
          ? { moduleType, category: validCategories[0] }
          : { moduleType },
      });
      const updatedTrainings = useTrainingStore
        .getState()
        .trainings.filter(
          (t) =>
            t?.moduleType === moduleType ||
            (isFixedCategory && t?.category === validCategories[0]),
        );
      const trainingData = updatedTrainings.find((t: Training) =>
        t.tests?.some((tTest: Test) => tTest.id === id),
      );
      if (!trainingData) {
        return;
      }
      setTraining(trainingData);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const trainingDate = new Date(trainingData.date);
      trainingDate.setHours(0, 0, 0, 0);
      const testCategory = trainingDate >= today ? "Pre-Test" : "Post-Test";

      const relevantTest = trainingData.tests?.find(
        (tTest: Test) => tTest.id === id,
      );
      if (!relevantTest) {
        return;
      }
      setTest(relevantTest);
      setTestCategory(testCategory);

      const traineeList = trainingData.trainees || [];
      const traineeData: TraineeWithResults[] = traineeList.map(
        (trainee: Trainee) => {
          const preTestScore = trainee.results?.preTestScore ?? null;
          const postTestScore = trainee.results?.postTestScore ?? null;
          let attendanceObj: Record<string, "Present" | "Absent"> = {
            "Pre-Test": trainee.attendance?.["Pre-Test"] || "Absent",
            "Post-Test": trainee.attendance?.["Post-Test"] || "Absent",
          };
          return {
            ...trainee,
            preTestScore,
            postTestScore,
            attendance: attendanceObj,
          };
        },
      );
      setTrainees(traineeData);
      setAttendance(
        traineeData.reduce(
          (
            acc: Record<string, "Present" | "Absent">,
            trainee: TraineeWithResults,
          ) => ({
            ...acc,
            [trainee.traineeId]: trainee.attendance[testCategory] || "Absent",
          }),
          {},
        ),
      );
    };
    fetchData();
  }, [id, fetchTrainings]);

  const toggleAttendance = (
    traineeId: string,
    status: "Present" | "Absent",
  ) => {
    if (!test) return;
    setAttendance((prev) => ({
      ...prev,
      [traineeId]: status,
    }));

    setTrainees((prev) => {
      const newTrainees = prev.map((trainee: TraineeWithResults) => {
        if (trainee.traineeId === traineeId) {
          return {
            ...trainee,
            attendance: {
              ...trainee.attendance,
              [test.testCategory]: status,
            },
          };
        }
        return trainee;
      });
      return newTrainees;
    });
  };

  const markAllPresent = () => {
    if (!test) return;

    setAttendance((prev) => {
      const newAttendance: Record<string, "Present" | "Absent"> = { ...prev };
      trainees.forEach((trainee: TraineeWithResults) => {
        newAttendance[trainee.traineeId] = "Present";
      });
      return newAttendance;
    });

    setTrainees((prev) => {
      const newTrainees = prev.map((trainee: TraineeWithResults) => ({
        ...trainee,
        attendance: {
          ...trainee.attendance,
          [test.testCategory]: "Present" as "Present" | "Absent",
        },
      }));
      if (gridApi) {
        gridApi.refreshCells({ force: true });
        setGridKey((prev) => prev + 1);
      }
      return newTrainees;
    });
  };

  const sendEmailsToPresentTrainees = async () => {
    if (!training || !test) return;

    const presentTrainees = trainees.filter(
      (trainee) => attendance[trainee.traineeId] === "Present",
    );

    if (presentTrainees.length === 0) {
      return;
    }

    const loginUrl = `${window.location.origin}/url/login`; // Redirect to login page
    const templateFileName =
      test.testCategory === "Pre-Test"
        ? "pretest_notification.json"
        : "posttest_notification.json";

    const emailPromises = presentTrainees.map(async (trainee) => {
      // Call generatePDF to get the pre-signed URL
      try {
        const pdfResponse = await axiosInstance.get(
          `/api/test/${test.id}/generate-pdf`
        );
        const pdfLink = pdfResponse.data.message; // Pre-signed URL from response

        const variables = [
          { key: "traineeName", value: trainee.name },
          { key: "topic", value: training.topic },
          { key: "testCategory", value: test.testCategory },
          { key: "date", value: formatDate(training.date) },
          { key: "testLink", value: loginUrl }, // Use login URL
          { key: "pdfLink", value: pdfLink }, // Use pre-signed URL
        ];

        const response = await axiosInstance.post(
          `/api/sendemail`,
          {
            templateFileName,
            to: [trainee.email],
            cc: [],
            bcc: [],
            variables,
          }
        );
      } catch (error) {
        console.error(
          `Failed to send email to ${trainee.email}:`,
          error instanceof Error ? error.message : String(error),
        );
      }
    });

    await Promise.all(emailPromises);
  };

  const handleUpdate = async () => {
    if (!training || !training.id || !test) {
      return;
    }

    const updates = trainees
      .map((trainee: TraineeWithResults) => ({
        traineeId: trainee.traineeId,
        attendance: attendance[trainee.traineeId] as "Present" | "Absent",
        testCategory: test.testCategory,
        tenantId: training.tenantId,
      }))
      .filter((update) => {
        const trainee = training.trainees.find(
          (t: Trainee) => t.traineeId === update.traineeId,
        );
        const currentAttendance =
          trainee?.attendance?.[test.testCategory] || "Absent";
        return update.attendance !== currentAttendance;
      });

    if (updates.length === 0) {
      setShowPopup(false);
      return;
    }

    try {
      await updateAttendance(training.id, updates, training.tenantId);
      await sendEmailsToPresentTrainees(); // Send emails after attendance update
      setShowPopup(false);
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (
        typeof error === "object" &&
        error !== null &&
        "response" in error
      ) {
        errorMessage =
          (error as any).response?.data?.error || "Failed to update attendance";
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const columnDefs: ColDef<TraineeWithResults>[] = [
    { headerName: "ID", field: "traineeEmployeeId", flex: 1 },
    { headerName: "Name", field: "name", flex: 1 },
    {
      headerName: "Pre-Test Score",
      field: "preTestScore",
      flex: 1,
      valueFormatter: (params) =>
        params.value != null ? `${Math.round(params.value)}%` : "-",
    },
    {
      headerName: "Post-Test Score",
      field: "postTestScore",
      flex: 1,
      valueFormatter: (params) =>
        params.value != null ? `${params.value}%` : "-",
    },
    {
      headerName: "Pre-Test Attendance",
      field: "attendance",
      flex: 1,
      cellRenderer: (params: { data: TraineeWithResults }) => {
        return (
          <div>
            {params.data.attendance["Pre-Test"] === "Present" ? (
              <span className="text-green-500">✔</span>
            ) : (
              <span className="text-red-500">✘</span>
            )}
          </div>
        );
      },
    },
    {
      headerName: "Post-Test Attendance",
      field: "attendance",
      flex: 1,
      cellRenderer: (params: { data: TraineeWithResults }) => {
        return (
          <div>
            {params.data.attendance["Post-Test"] === "Present" ? (
              <span className="text-green-500">✔</span>
            ) : (
              <span className="text-red-500">✘</span>
            )}
          </div>
        );
      },
    },
  ];

  if (!training || !test) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading... or data not found
      </div>
    );
  }

  return (
    <div className="px-2 py-4 space-y-6 mt-14">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">Attendance</h2>
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          <button
            onClick={() => setShowPopup(true)}
            className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#28a745] text-white whitespace-nowrap"
          >
            Mark Attendance for {test.testCategory}
          </button>
          <Link to={paths.dashboard}>
            <button className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white whitespace-nowrap">
              <ArrowLeft className="mr-2 h-4 w-4" />
              BACK
            </button>
          </Link>
        </div>
      </div>
      <div className="flex justify-between items-center gap-4 mb-4 bg-white px-4 py-3 shadow-xl">
        <div>
          <p className="text-lg text-black font-bold">Topic</p>
          <p className="font-medium text-black">{training.topic}</p>
        </div>
        <div>
          <p className="text-lg text-black font-bold">Category</p>
          <p className="font-medium text-black">{test?.testCategory || "-"}</p>
        </div>
        <div>
          <p className="text-lg text-black font-bold">Date</p>
          <p className="font-medium text-black">{formatDate(training.date)}</p>
        </div>
        <div>
          <p className="text-lg text-black font-bold">Total Trainees</p>
          <p className="font-medium text-black">
            {training.trainees?.length || 0}
          </p>
        </div>
      </div>
      <div className="ag-theme-alpine">
        <AgGridReact
          key={gridKey}
          ref={gridRef}
          rowData={trainees}
          columnDefs={columnDefs}
          rowHeight={50}
          headerHeight={65}
          paginationPageSize={20}
          pagination={true}
          domLayout="autoHeight"
          defaultColDef={{
            sortable: true,
            resizable: true,
          }}
          getRowId={(params) => params.data.traineeId}
          onGridReady={onGridReady}
          getRowStyle={() => ({
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginBottom: "8px",
            backgroundColor: "white",
          })}
        />
      </div>
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="p-8 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-800">
                Mark Attendance for {test.testCategory}
              </h2>
              {/* Toggle for Mark All Present */}
              <label className="relative inline-block w-16 h-7 ml-4">
                <input
                  type="checkbox"
                  checked={
                    trainees.length > 0 &&
                    trainees.every(
                      (trainee) => attendance[trainee.traineeId] === "Present",
                    )
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      markAllPresent();
                    } else {
                      // Mark all as absent
                      setAttendance((prev) => {
                        const newAttendance: Record<
                          string,
                          "Present" | "Absent"
                        > = { ...prev };
                        trainees.forEach((trainee) => {
                          newAttendance[trainee.traineeId] = "Absent";
                        });
                        return newAttendance;
                      });
                      setTrainees((prev) =>
                        prev.map((trainee) => ({
                          ...trainee,
                          attendance: {
                            ...trainee.attendance,
                            [test.testCategory]: "Absent",
                          },
                        })),
                      );
                    }
                  }}
                  className="sr-only peer"
                />
                <div
                  className={`w-full h-full rounded-full transition-colors duration-300 ${trainees.length > 0 &&
                      trainees.every(
                        (trainee) => attendance[trainee.traineeId] === "Present",
                      )
                      ? "bg-green-500"
                      : "bg-red-400"
                    }`}
                ></div>
                <span
                  className={`absolute inset-y-0 left-0 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center text-xs font-bold text-white transition-transform duration-300
                    ${trainees.length > 0 &&
                      trainees.every(
                        (trainee) =>
                          attendance[trainee.traineeId] === "Present",
                      )
                      ? "translate-x-9 bg-green-500"
                      : "translate-x-0 bg-red-400"
                    }`}
                >
                  {trainees.length > 0 &&
                    trainees.every(
                      (trainee) => attendance[trainee.traineeId] === "Present",
                    )
                    ? "All P"
                    : "All A"}
                </span>
              </label>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {trainees.map((trainee) => (
                <div
                  key={trainee.traineeId}
                  className="flex justify-between items-center border border-gray-300 rounded-xl px-4 py-3 hover:shadow-md transition-shadow bg-gray-50"
                >
                  <span className="text-gray-800 font-medium">
                    {trainee.name}
                  </span>
                  <label className="relative inline-block w-12 h-6">
                    <input
                      type="checkbox"
                      checked={attendance[trainee.traineeId] === "Present"}
                      onChange={() =>
                        toggleAttendance(
                          trainee.traineeId,
                          attendance[trainee.traineeId] === "Present"
                            ? "Absent"
                            : "Present",
                        )
                      }
                      className="sr-only peer"
                    />
                    <div
                      className={`w-full h-full rounded-full transition-colors duration-300 ${attendance[trainee.traineeId] === "Present"
                          ? "bg-green-500"
                          : "bg-red-400"
                        }`}
                    ></div>
                    <span
                      className={`absolute inset-y-0 left-0 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center text-xs font-bold text-white transition-transform duration-300
                        ${attendance[trainee.traineeId] === "Present"
                          ? "translate-x-6 bg-green-500"
                          : "translate-x-0 bg-red-400"
                        }`}
                    >
                      {attendance[trainee.traineeId] === "Present" ? "P" : "A"}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            </div>
            <div className="flex justify-end gap-4 bg-[#5a687c] px-8 py-4">
              <button
                onClick={() => setShowPopup(false)}
                className="px-6 py-2 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-6 py-2 rounded-full bg-[#0fa9a6] text-white hover:bg-teal-700 transition"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartTest;
